import React from "react";
import { Lock, ShieldCheck, KeyRound, FileCheck2, Server, Eye, Database, Users } from "lucide-react";
import { useI18n } from "../../i18n";
import { Container, Section, Card, Badge } from "../../components/ui/Primitives";
import { Button } from "../../components/ui/Button";
import { PageHero, CTASection } from "../../components/marketing/Shared";

const PILLARS = [
  { icon: Lock, title: "Encryption everywhere", desc: "TLS 1.3 in transit and AES-256 at rest for recordings, metadata and exports." },
  { icon: Eye, title: "Clear recording notice", desc: "Monitored users always receive an explicit, logged consent and recording notice." },
  { icon: KeyRound, title: "SSO, SAML & MFA", desc: "OAuth/OIDC, SAML 2.0 and enforced multi-factor authentication for staff." },
  { icon: Database, title: "Retention & export controls", desc: "Configurable retention windows, data export and right-to-erasure workflows." },
  { icon: Server, title: "Tenant isolation", desc: "Every session, recording and report is scoped to its company (tenant)." },
  { icon: Users, title: "Role-based access", desc: "Admin, proctor, reviewer and viewer roles with least-privilege defaults." },
];

const CERTS = [
  { label: "SOC 2-ready controls", tone: "success" },
  { label: "LGPD aligned", tone: "success" },
  { label: "GDPR aligned", tone: "success" },
  { label: "DPA available", tone: "brand" },
];

export default function Security() {
  const { t } = useI18n();
  return (
    <>
      <PageHero
        breadcrumb={t("nav.security")}
        eyebrow={t("security.hero.eyebrow")}
        title={t("security.hero.title")}
        lead={t("security.hero.lead")}
      >
        <div className="flex flex-wrap gap-2">
          {CERTS.map((c) => (
            <Badge key={c.label} tone={c.tone} className="px-3 py-1">
              <ShieldCheck className="h-3.5 w-3.5" /> {c.label}
            </Badge>
          ))}
        </div>
      </PageHero>

      <Section>
        <Container>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PILLARS.map((p) => (
              <Card key={p.title} className="p-6">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-success/12 text-success">
                  <p.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-base font-bold">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      <Section alt>
        <Container>
          <Card className="mx-auto flex max-w-3xl flex-col items-center gap-4 p-8 text-center">
            <FileCheck2 className="h-10 w-10 text-brand" />
            <h2 className="text-2xl">Data Processing Agreement</h2>
            <p className="max-w-xl text-muted-foreground">
              We provide a standard DPA covering roles, sub-processors, security measures and data-subject
              rights. Enterprise customers can request custom terms and a security questionnaire.
            </p>
            <Button to="/contato">{t("common.contactSales")}</Button>
          </Card>
        </Container>
      </Section>

      <CTASection />
    </>
  );
}
